import { Fragment, useState, type FormEvent, type KeyboardEvent } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { ChevronRight, ExternalLink, ImageOff, LoaderCircle, MoreHorizontal, Play, Plus, RefreshCw } from "lucide-react";
import { api, formatDate, formatDateTime } from "../api";
import { useAuth } from "../auth";
import { useClientSelection } from "../client-context";
import { useApi } from "../hooks";
import {
  LEVELS, activeNote, checkState, cleanAdUrl, compactCount, daysRunning, daysText, displayCopy, FUNNEL_LABEL, funnelKind, isTemplated, isVideoAd,
  groupAds, landingKey, levelLabel, perScanText, platformsLabel, providerNote, scanCostLine, sendsLines,
  type AdsResponse, type Competitor, type CompetitorAd, type CompetitorsResponse, type ScanRow,
} from "../competitors";
import Dialog from "../components/Dialog";
import QuickMenu, { QuickMenuItem } from "../components/QuickMenu";
import { EmptyState, ErrorState, PageHeader, PageSkeleton } from "../components/Ui";
import { NoClient } from "../components/Workflow";
import { FindView, PostsView } from "../components/competitors/SocialViews";

/**
 * Competitors: who a client competes with on Facebook and Instagram, read from Meta's Ad Library,
 * and how they compare. The page never scans on its own: a scan costs money, so it is a button.
 * See utils/competitors.js for the status rules this page words.
 */
export default function Competitors() {
  const { clientId } = useClientSelection();
  const [params] = useSearchParams();
  // Competitors' viral posts live on Viral posts (its From competitors tab); this page's own copy
  // of that tab is gone, and an old link to it goes there.
  if (params.get("view") === "outliers") {
    const next = new URLSearchParams(params);
    next.set("view", "competitors");
    return <Navigate replace to={`/viral?${next}`} />;
  }
  if (!clientId) return <><PageHeader title="Competitors" description="Pick a client in the sidebar." /><NoClient /></>;
  return <CompetitorsPage key={clientId} clientId={clientId} />;
}

type Pending =
  | { kind: "add" }
  | { kind: "edit"; competitor: Competitor }
  | { kind: "remove"; competitor: Competitor }
  | { kind: "scan-all" };

type Notice = { tone: "success" | "danger" | "info"; text: string; link?: { to: string; label: string } } | null;
type View = "compare" | "posts" | "find";
const VIEWS: Array<{ id: View; label: string }> = [{ id: "compare", label: "Compare" }, { id: "posts", label: "Their posts" }, { id: "find", label: "Find competitors" }];

function CompetitorsPage({ clientId }: { clientId: number }) {
  const { user } = useAuth();
  const { client } = useClientSelection();
  const { data, loading, error, reload, setData } = useApi<CompetitorsResponse>(`/api/competitors?client_id=${clientId}`);
  const [open, setOpen] = useState<number | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [scanning, setScanning] = useState<Set<number>>(new Set());
  const [scanningAll, setScanningAll] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const [adsVersion, setAdsVersion] = useState(0);
  const [params, setParams] = useSearchParams();
  const view: View = (VIEWS.find((v) => v.id === params.get("view"))?.id) || "compare";
  const setView = (v: View) => setParams((cur) => { const n = new URLSearchParams(cur); if (v === "compare") n.delete("view"); else n.set("view", v); return n; }, { replace: true });

  if (loading && !data) return <><PageHeader title="Competitors" /><PageSkeleton cards={0} /></>;
  if (error || !data) return <><PageHeader title="Competitors" /><ErrorState message={error || "Competitors could not load."} onRetry={reload} /></>;

  const rows = data.competitors;
  const self = rows.find((r) => r.kind === "self") || null;
  const name = client?.name || data.client.name;
  const perScan = perScanText(data.cost);
  const canChange = data.can_change;
  const note = providerNote(data.provider, data.cost);

  const patchRow = (row: Competitor) => setData((d) => (d ? { ...d, competitors: d.competitors.map((c) => (c.id === row.id ? { ...c, ...row, stats: row.stats !== undefined ? row.stats : c.stats } : c)) } : d));

  async function scanOne(c: Competitor) {
    setNotice(null);
    setScanning((s) => new Set(s).add(c.id));
    try {
      const r = await api<{ scan: { status: string; ads_found: number; detail: string | null }; competitor: Competitor | null }>(`/api/competitors/${c.id}/scan`, { method: "POST", body: "{}" });
      if (r.competitor) patchRow(r.competitor);
      const who = r.competitor?.label || c.label;
      if (r.scan.status === "ok") setNotice({ tone: "success", text: `${who}: read ${r.scan.ads_found} active ad${r.scan.ads_found === 1 ? "" : "s"}.` });
      else if (r.scan.status === "empty") setNotice({ tone: "info", text: `${who}: no active ads right now.` });
      else setNotice({ tone: "danger", text: `${who}: could not check. ${r.scan.detail || ""}`.trim() });
      setAdsVersion((v) => v + 1);
    } catch (e) {
      setNotice({ tone: "danger", text: e instanceof Error ? e.message : "The check did not run." });
    } finally {
      setScanning((s) => { const n = new Set(s); n.delete(c.id); return n; });
      reload();
    }
  }

  async function scanAll() {
    setPending(null);
    setNotice(null);
    setScanningAll(true);
    try {
      const r = await api<{ results: { status: string }[]; not_reached: number[] }>("/api/competitors/scan-all", { method: "POST", body: JSON.stringify({ client_id: clientId }) });
      const failed = r.results.filter((x) => x.status === "blocked" || x.status === "error").length;
      const parts = [`Checked ${r.results.length} of ${r.results.length + r.not_reached.length}.`];
      if (failed) parts.push(`${failed} could not be checked; see Last checked.`);
      if (r.not_reached.length) parts.push("Press Scan all again for the rest.");
      setNotice({ tone: failed ? "danger" : "success", text: parts.join(" ") });
      setAdsVersion((v) => v + 1);
    } catch (e) {
      setNotice({ tone: "danger", text: e instanceof Error ? e.message : "The checks did not run." });
    } finally {
      setScanningAll(false);
      reload();
    }
  }

  async function toggleWatch(c: Competitor) {
    setNotice(null);
    try {
      const row = await api<Competitor>(`/api/competitors/${c.id}`, { method: "PATCH", body: JSON.stringify({ watching: !c.watching }) });
      patchRow({ ...row, stats: c.stats });
      setNotice({ tone: "success", text: row.watching ? `${row.label} is checked every ${data!.cost.rescan_days} days again.` : `Weekly checks of ${row.label} are paused.` });
    } catch (e) {
      setNotice({ tone: "danger", text: e instanceof Error ? e.message : "Could not save." });
    }
  }

  const header = (
    <PageHeader
      title="Competitors"
      description={`Who ${name} competes with on Facebook and Instagram: their ads, their posts, and what is working for them.`}
      actions={canChange ? <>
        {rows.length > 0 && (
          <button type="button" className="button secondary" disabled={!data.ready || scanningAll} title={`Reads each competitor's active ads now. ${scanCostLine(rows.length, data.cost)}.`} onClick={() => setPending({ kind: "scan-all" })}>
            <RefreshCw className={scanningAll ? "spin" : ""} aria-hidden="true" />{scanningAll ? "Scanning…" : "Scan all"}
          </button>
        )}
        <button type="button" className="button" onClick={() => { setNotice(null); setPending({ kind: "add" }); }}><Plus aria-hidden="true" />Add competitor</button>
      </> : undefined}
    />
  );

  return (
    <div className="competitors-page">
      {header}
      {!data.ready && (
        <p className="inline-notice warning competitors-connect" role="status">
          <span>No ad scanner is connected, so competitors cannot be checked yet. Saved results still show.</span>
          {user?.role === "superadmin" ? <Link className="text-button" to="/settings#integrations">Add a Scrape Creators key in Settings</Link> : <span className="muted">An admin adds the Scrape Creators key in Settings, Integrations.</span>}
        </p>
      )}
      {notice && (
        <p className={`inline-notice ${notice.tone === "info" ? "" : notice.tone} competitors-notice`} role={notice.tone === "danger" ? "alert" : "status"}>
          <span>{notice.text}</span>
          {notice.link && <Link className="text-button" to={notice.link.to}>{notice.link.label}</Link>}
          <button type="button" className="competitors-dismiss" aria-label="Dismiss" onClick={() => setNotice(null)}>×</button>
        </p>
      )}

      <div className="segmented-control competitors-views" role="tablist" aria-label="Competitor views">
        {VIEWS.map((v) => <button key={v.id} type="button" role="tab" aria-selected={view === v.id} className={view === v.id ? "active" : ""} onClick={() => setView(v.id)}>{v.label}</button>)}
      </div>
      {view === "posts" ? (
        <PostsView key={view} clientId={clientId} competitors={rows} canChange={canChange} outliersOnly={false} onCompetitorsChanged={reload} />
      ) : view === "find" ? (
        <FindView clientId={clientId} defaults={data.find_defaults || { service: "", area: "" }} canChange={canChange} onAdded={(label, hasSocial) => { reload(); setNotice({ tone: "success", text: `${label} added. Check ads on Compare.${hasSocial ? ' Read their posts on Posts.' : ' Add an Instagram handle or Facebook page to read posts.'}` }); }} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No competitors yet"
          description={`Use Find competitors to search ${name}'s area, or paste a competitor's Facebook page to see the ads they run and how they compare.`}
          action={canChange ? <button type="button" className="button" onClick={() => setPending({ kind: "add" })}><Plus aria-hidden="true" />Add competitor</button> : undefined}
        />
      ) : (
        <section className="panel competitors-panel">
          <div className="table-wrap">
            <table className="ads-table competitors-table">
              <thead><tr>
                <th>Name</th>
                <th className="num competitors-col-likes">Page likes</th>
                <th className="num">Active ads</th>
                <th className="num">New in 7 days</th>
                <th className="num">Longest running</th>
                <th className="num">Video / image</th>
                <th>Where ads send</th>
                <th>Last checked</th>
                <th><span className="sr-only">Actions</span></th>
              </tr></thead>
              <tbody>{rows.map((c) => {
                const isOpen = open === c.id;
                const toggle = () => setOpen(isOpen ? null : c.id);
                return (
                  <Fragment key={c.id}>
                    <CompetitorRow
                      c={c} isOpen={isOpen} onToggle={toggle} canChange={canChange} ready={data.ready} perScan={perScan}
                      scanning={scanning.has(c.id) || c.scanning || (scanningAll && !c.stats)}
                      onScan={() => scanOne(c)} onWatch={() => toggleWatch(c)}
                      onEdit={() => setPending({ kind: "edit", competitor: c })} onRemove={() => setPending({ kind: "remove", competitor: c })}
                    />
                    {isOpen && (
                      <tr className="competitors-detail-row"><td colSpan={9}>
                        <CompetitorAds key={`${c.id}-${adsVersion}`} c={c} canChange={canChange} ready={data.ready} perScan={perScan} busy={scanning.has(c.id)} onScan={() => scanOne(c)} onNotice={setNotice} />
                      </td></tr>
                    )}
                  </Fragment>
                );
              })}</tbody>
            </table>
          </div>
        </section>
      )}
      {view === "compare" && rows.length > 0 && <p className="competitors-footnote">Numbers are among the active ads each check read (up to {data.cost.ads_per_scan} per competitor). Watched competitors are checked again every {data.cost.rescan_days} days.{note ? ` ${note}` : ""}</p>}

      {pending?.kind === "add" && (
        <AddDialog clientId={clientId} clientName={name} hasSelf={Boolean(self)} onClose={() => setPending(null)}
          onAdded={(row) => { setPending(null); reload(); setNotice({ tone: "success", text: `${row.kind === "self" ? `${name}'s own page` : row.label} added. ${data.ready ? `Press Scan now to read their ads (${perScan}), or wait for the weekly check.` : "It is checked once an ad scanner is connected in Settings."}` }); }} />
      )}
      {pending?.kind === "edit" && (
        <EditDialog c={pending.competitor} onClose={() => setPending(null)} onSaved={(row) => { setPending(null); patchRow({ ...row, stats: pending.competitor.stats }); }} />
      )}
      {pending?.kind === "remove" && (
        <RemoveDialog c={pending.competitor} onClose={() => setPending(null)} onRemoved={() => { setPending(null); if (open === pending.competitor.id) setOpen(null); reload(); setNotice({ tone: "success", text: `${pending.competitor.label} removed.` }); }} />
      )}
      {pending?.kind === "scan-all" && (
        <Dialog title={`Scan all ${rows.length} ${rows.length === 1 ? "competitor" : "competitors"}?`} onClose={() => setPending(null)}>
          <div className="operation-form">
            <p className="competitors-dialog-copy">Reads up to {data.cost.ads_per_scan} active ads each from Meta's Ad Library through {data.provider?.label || "the ad scanner"}. {scanCostLine(rows.length, data.cost)}{data.cost.provider === "scrapecreators" && data.cost.credits_left !== null ? ` (${data.cost.credits_left.toLocaleString("en-US")} left)` : ""}. It can take a few minutes.</p>
            <div className="operation-actions">
              <button type="button" className="button secondary" onClick={() => setPending(null)}>Cancel</button>
              <button type="button" className="button" onClick={scanAll}>Scan all</button>
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
}

function CompetitorRow({ c, isOpen, onToggle, canChange, ready, perScan, scanning, onScan, onWatch, onEdit, onRemove }: {
  c: Competitor; isOpen: boolean; onToggle: () => void; canChange: boolean; ready: boolean; perScan: string; scanning: boolean;
  onScan: () => void; onWatch: () => void; onEdit: () => void; onRemove: () => void;
}) {
  const s = c.stats;
  const state = checkState(c.last_scan_status);
  const lines = sendsLines(s?.sends);
  const note = activeNote(s);
  const onKey = (e: KeyboardEvent<HTMLTableRowElement>) => { if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) { e.preventDefault(); onToggle(); } };
  return (
    <tr className={`competitors-row${isOpen ? " open" : ""}${c.kind === "self" ? " self" : ""}${s ? "" : " no-stats"}`} onClick={onToggle} onKeyDown={onKey} tabIndex={0} aria-expanded={isOpen}>
      <td className="competitors-name-cell">
        <span className="competitors-name">
          <ChevronRight className="competitors-chevron" aria-hidden="true" />
          <span>
            <strong title={c.query}>{c.kind === "self" ? "You" : c.label}</strong>
            <small>{c.kind === "self" ? c.page_name || c.label : levelLabel(c.level)}{!c.watching && <span className="competitors-paused">, weekly check paused</span>}</small>
            {c.instagram_handle && <small className="competitors-ig">@{c.instagram_handle}{c.instagram_followers != null ? `, ${compactCount(c.instagram_followers)} followers` : ""}{c.social ? `, ${c.social.posts_30d} posts in 30 days${c.social.outliers ? `, ${c.social.outliers} ${c.social.outliers === 1 ? "outlier" : "outliers"}` : ""}` : ""}</small>}
          </span>
        </span>
      </td>
      <td className="num competitors-col-likes" data-label="Page likes">{compactCount(c.page_likes)}</td>
      <td className="num" data-label="Active ads">{s ? <>{s.active.toLocaleString("en-US")}{note && <small>{note}</small>}</> : "—"}</td>
      <td className="num" data-label="New in 7 days">{s ? s.new_7d : "—"}</td>
      <td className="num" data-label="Longest running">{s ? daysText(s.longest_days) : "—"}</td>
      <td className="num" data-label="Video / image">{s && s.scanned ? `${s.video} / ${s.image}` : "—"}</td>
      <td data-label="Where ads send" className="competitors-sends-cell">
        {s && lines.length ? (
          <span className="competitors-sends">
            {lines.map((l) => <span key={l.kind}>{l.label} <b>{l.count}</b></span>)}
            {/* The top landing page: the page most of their website ads send people to. */}
            {s.top_landing && (
              <a className="competitors-landing" href={s.top_landing.url} target="_blank" rel="noreferrer noopener" title={`Top landing page, ${s.top_landing.count} ${s.top_landing.count === 1 ? "ad" : "ads"}: ${s.top_landing.url}`} onClick={(e) => e.stopPropagation()}>{s.top_landing.label}</a>
            )}
          </span>
        ) : "—"}
      </td>
      <td data-label="Last checked" className="competitors-check-cell">
        {scanning ? <span className="competitors-checking"><LoaderCircle className="spin" aria-hidden="true" />Checking…</span> : (
          <span className="competitors-check">
            <span className={`competitors-state ${state.tone}`}>{state.label}</span>
            {c.last_scanned_at && <small>{formatDate(c.last_scanned_at)}</small>}
            {canChange && ready && (c.last_scan_status === "blocked" || c.last_scan_status === "error") && <button type="button" className="text-button" onClick={(e) => { e.stopPropagation(); onScan(); }}>Retry</button>}
            {canChange && ready && !c.last_scan_status && <button type="button" className="text-button" title={perScan} onClick={(e) => { e.stopPropagation(); onScan(); }}>Scan now</button>}
          </span>
        )}
      </td>
      <td className="competitors-actions" onClick={(e) => e.stopPropagation()}>
        <QuickMenu align="right" label={`Actions for ${c.label}`} trigger={<MoreHorizontal aria-hidden="true" />}>
          {(close) => (<>
            {canChange && <QuickMenuItem onClick={() => { close(); if (ready && !scanning) onScan(); }}>{!ready ? "Scan now (no scanner connected)" : scanning ? "Checking…" : `Scan now, ${perScan}`}</QuickMenuItem>}
            {canChange && <QuickMenuItem onClick={() => { close(); onWatch(); }}>{c.watching ? "Pause weekly check" : "Resume weekly check"}</QuickMenuItem>}
            {canChange && <QuickMenuItem onClick={() => { close(); onEdit(); }}>{c.kind === "self" ? "Rename" : "Change level or name"}</QuickMenuItem>}
            <QuickMenuItem onClick={() => { close(); window.open(c.ad_library_url, "_blank", "noopener,noreferrer"); }}>Open in Ad Library</QuickMenuItem>
            {canChange && <QuickMenuItem tone="danger" onClick={() => { close(); onRemove(); }}>Remove</QuickMenuItem>}
          </>)}
        </QuickMenu>
      </td>
    </tr>
  );
}

/** One competitor's ads, under its row. Reads the saved ads only; never scans by itself. */
/** Cards shown before "Show all": enough to read their messages without a wall of ads. */
const FIRST_CARDS = 12;

function CompetitorAds({ c, canChange, ready, perScan, busy, onScan, onNotice }: {
  c: Competitor; canChange: boolean; ready: boolean; perScan: string; busy: boolean; onScan: () => void; onNotice: (n: Notice) => void;
}) {
  const [sort, setSort] = useState<"longest" | "newest">("longest");
  const [scope, setScope] = useState<"active" | "all">("active");
  const [history, setHistory] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const { data, loading, error, reload } = useApi<AdsResponse>(`/api/competitors/${c.id}/ads?sort=${sort}&scope=${scope}`, { keepPrevious: true });
  const state = checkState(c.last_scan_status);
  const s = c.stats;
  const groups = data ? groupAds(data.ads) : [];
  const shown = showAll ? groups : groups.slice(0, FIRST_CARDS);

  return (
    <div className="competitors-detail">
      {state.unknown && c.last_scan_status && (
        <div className="inline-notice warning competitors-detail-note" role="status">
          <span><strong>Could not check{c.last_scanned_at ? ` on ${formatDate(c.last_scanned_at)}` : ""}.</strong> This says nothing about whether they are advertising. {c.last_scan_detail}</span>
          {canChange && ready && <button type="button" className="text-button" disabled={busy} onClick={onScan}>{busy ? "Checking…" : "Retry"}</button>}
        </div>
      )}
      {!c.last_scan_status && (
        <p className="competitors-detail-empty">
          Not checked yet.{canChange && ready ? <> <button type="button" className="text-button" disabled={busy} onClick={onScan}>{busy ? "Checking…" : `Scan now, ${perScan}`}</button></> : ready ? "" : " Connect an ad scanner in Settings to check them."}
        </p>
      )}

      {s && (
        <div className="competitors-detail-head">
          <p>
            {s.scanned > 0
              ? <>Read {formatDate(s.as_of)}: {s.scanned} active {s.scanned === 1 ? "ad" : "ads"}{s.active_from_meta && s.active > s.scanned ? ` of the ${s.active.toLocaleString("en-US")} Meta lists` : ""}{scope === "active" && groups.length && groups.length < s.scanned ? `, with ${groups.length} different ${groups.length === 1 ? "message" : "messages"}` : ""}. Running longest is how long an ad has been live, a hint at what works, not a result.</>
              : <>Checked {formatDate(s.as_of)}: no active ads in the Ad Library.</>}
          </p>
          <div className="competitors-detail-controls">
            <label className="competitors-select"><span className="sr-only">Sort ads</span>
              <select value={sort} onChange={(e) => setSort(e.target.value as "longest" | "newest")} aria-label="Sort ads">
                <option value="longest">Running longest</option>
                <option value="newest">Newest</option>
              </select>
            </label>
            {data && data.counts.total > data.counts.active && (
              <button type="button" className="text-button" onClick={() => setScope(scope === "active" ? "all" : "active")}>
                {scope === "active" ? `Show ${data.counts.total - data.counts.active} seen in earlier checks` : "Active ads only"}
              </button>
            )}
          </div>
        </div>
      )}

      {loading && !data ? <p className="competitors-detail-empty"><LoaderCircle className="spin" aria-hidden="true" /> Loading their ads…</p>
        : error ? <ErrorState message={error} onRetry={reload} />
        : data && data.ads.length ? (
          <>
            <ul className="competitors-ads">{shown.map((g) => <AdCard key={g.key} ad={g.lead} count={g.ads.length} canChange={canChange} onNotice={onNotice} />)}</ul>
            {groups.length > shown.length && <button type="button" className="button secondary competitors-show-all" onClick={() => setShowAll(true)}>Show all {groups.length}</button>}
          </>
        ) : data && data.counts.total > 0 && scope === "active" ? (
          <p className="competitors-detail-empty">None of their saved ads were in the latest check. <button type="button" className="text-button" onClick={() => setScope("all")}>Show the {data.counts.total} seen before</button></p>
        ) : null}

      <div className="competitors-history">
        <button type="button" className="text-button" aria-expanded={history} onClick={() => setHistory(!history)}>{history ? "Hide check history" : "Check history"}</button>
        {history && <ScanHistory id={c.id} />}
      </div>
    </div>
  );
}

function ScanHistory({ id }: { id: number }) {
  const { data, loading, error } = useApi<ScanRow[]>(`/api/competitors/${id}/scans`);
  if (loading) return <p className="competitors-detail-empty">Loading…</p>;
  if (error || !data) return <p className="competitors-detail-empty">{error || "Could not load."}</p>;
  if (!data.length) return <p className="competitors-detail-empty">No checks yet.</p>;
  return (
    <ul className="competitors-scans">
      {data.map((s) => {
        const st = checkState(s.status);
        return (
          <li key={s.id}>
            <span className={`competitors-state ${st.tone}`}>{s.status === "ok" ? `${s.ads_found} ${s.ads_found === 1 ? "ad" : "ads"} read` : st.label}</span>
            <span className="muted">{formatDateTime(s.started_at)}{s.triggered_by === "cron" ? ", weekly check" : s.by_name ? `, ${s.by_name}` : ""}{s.provider === "scrapecreators" ? ", Scrape Creators" : ", Apify"}</span>
            {s.detail && <p>{s.detail}</p>}
          </li>
        );
      })}
    </ul>
  );
}

function AdMedia({ ad }: { ad: CompetitorAd }) {
  const [playing, setPlaying] = useState(false);
  const [dead, setDead] = useState(false);
  const [posterDead, setPosterDead] = useState(false);
  const video = isVideoAd(ad) && ad.video_url;
  const poster = ad.preview_image_url || ad.image_url;
  const expired = (what: string) => (
    <div className="competitors-media-dead">
      {poster && !posterDead && <img src={poster} alt="" loading="lazy" onError={() => setPosterDead(true)} />}
      <div><ImageOff aria-hidden="true" /><span>{what} link has expired. Meta's links only last a few days after a check.</span>
        <a href={ad.ad_library_url} target="_blank" rel="noreferrer noopener">See it in the Ad Library</a></div>
    </div>
  );
  if (video) {
    if (dead) return expired("This video's");
    if (playing) return <video className="competitors-media-el" src={ad.video_url!} poster={poster || undefined} controls autoPlay playsInline preload="metadata" onError={() => setDead(true)} />;
    return (
      <button type="button" className="competitors-media-poster" onClick={() => setPlaying(true)} aria-label="Play this ad's video">
        {poster && !posterDead ? <img src={poster} alt="" loading="lazy" onError={() => setPosterDead(true)} /> : <span className="competitors-media-blank" />}
        <span className="competitors-play"><Play aria-hidden="true" /></span>
      </button>
    );
  }
  if (!ad.image_url) return <div className="competitors-media-dead"><div><ImageOff aria-hidden="true" /><span>No image in this ad.</span></div></div>;
  if (dead) return expired("This image's");
  return <img className="competitors-media-el" src={ad.image_url} alt="" loading="lazy" onError={() => setDead(true)} />;
}

function AdCard({ ad, count, canChange, onNotice }: { ad: CompetitorAd; count: number; canChange: boolean; onNotice: (n: Notice) => void }) {
  const [more, setMore] = useState(false);
  const [writing, setWriting] = useState(false);
  const headline = displayCopy(ad.headline);
  const body = displayCopy(ad.primary_text);
  const dynamic = (isTemplated(ad.headline) && !headline) || (isTemplated(ad.primary_text) && !body);
  const days = daysRunning(ad.started_running_at);
  const kind = funnelKind(ad.link_url, ad.cta_type);
  const link = kind === "website" ? cleanAdUrl(ad.link_url) : null;
  const long = (body || "").length > 180;
  const platforms = platformsLabel(ad.platforms);

  async function write() {
    setWriting(true);
    onNotice(null);
    try {
      const r = await api<{ draft_id: number; url: string }>(`/api/competitors/ads/${ad.id}/draft`, { method: "POST", body: "{}" });
      onNotice({ tone: "success", text: "Our version is saved as a draft in Content review.", link: { to: r.url, label: "Open draft" } });
    } catch (e) {
      onNotice({ tone: "danger", text: e instanceof Error ? e.message : "The draft could not be written." });
    } finally {
      setWriting(false);
    }
  }

  return (
    <li className={`competitors-ad${ad.active ? "" : " inactive"}`}>
      <div className="competitors-media"><AdMedia ad={ad} /></div>
      <div className="competitors-ad-body">
        {!ad.active && <small className="competitors-ad-old">Not in the latest check</small>}
        {headline && <strong className="competitors-ad-headline">{headline}</strong>}
        {body && <p className={`competitors-ad-text${more ? " more" : ""}`}>{body}</p>}
        {long && <button type="button" className="text-button competitors-more" onClick={() => setMore(!more)}>{more ? "Less" : "More"}</button>}
        {dynamic && <p className="competitors-ad-note">Dynamic ad: Meta fills in the words for each product, so the Ad Library shows a template.</p>}
        {!headline && !body && !dynamic && <p className="competitors-ad-note">No ad text. Image or video only.</p>}
        <dl className="competitors-ad-meta">
          <div><dt>Running</dt><dd>{ad.started_running_at ? `Since ${formatDate(ad.started_running_at)} (${daysText(days)})` : "Start date unknown"}</dd></div>
          <div><dt>Sends to</dt><dd>{link ? <a href={link} target="_blank" rel="noreferrer noopener" title={link}>{landingKey(link)}</a> : FUNNEL_LABEL[kind]}</dd></div>
          {ad.cta && <div><dt>Button</dt><dd>{ad.cta}</dd></div>}
          {count > 1 && <div><dt>Runs as</dt><dd>{count} ads with these words</dd></div>}
          {platforms && <div><dt>Shown on</dt><dd>{platforms}</dd></div>}
        </dl>
        <div className="competitors-ad-actions">
          <a className="text-button" href={ad.ad_library_url} target="_blank" rel="noreferrer noopener">View in Ad Library<ExternalLink aria-hidden="true" /></a>
          {canChange && (headline || body) && <button type="button" className="text-button" disabled={writing} onClick={write}>{writing ? "Writing…" : "Write our version"}</button>}
        </div>
      </div>
    </li>
  );
}

function LevelSelect({ value, onChange }: { value: number | null; onChange: (v: number | null) => void }) {
  return (
    <label>Level
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}>
        <option value="">Not ranked</option>
        {LEVELS.map((l) => <option key={l} value={l}>{levelLabel(l)}</option>)}
      </select>
    </label>
  );
}

function AddDialog({ clientId, clientName, hasSelf, onClose, onAdded }: { clientId: number; clientName: string; hasSelf: boolean; onClose: () => void; onAdded: (row: Competitor) => void }) {
  const [q, setQ] = useState("");
  const [level, setLevel] = useState<number | null>(null);
  const [own, setOwn] = useState(false);
  const [ig, setIg] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (q.trim().length < 2) { setErr("Paste their Facebook page link, or type their business name."); return; }
    setBusy(true);
    setErr("");
    try {
      const row = await api<Competitor>("/api/competitors", { method: "POST", body: JSON.stringify({ client_id: clientId, query: q.trim(), level: own ? null : level, kind: own ? "self" : "competitor", ...(ig.trim() ? { instagram_handle: ig.trim() } : {}) }) });
      onAdded(row);
    } catch (reason) {
      setErr(reason instanceof Error ? reason.message : "Could not add.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog title={own ? `Add ${clientName}'s page` : "Add competitor"} onClose={onClose}>
      <form className="operation-form competitors-form" onSubmit={submit}>
        <label>Facebook page link or name
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="facebook.com/theirpage, or their business name" autoComplete="off" />
          <small>A page link is exact. A name is matched to the advertiser with that page name.</small>
        </label>
        <label><span>Instagram <em>optional</em></span>
          <input value={ig} onChange={(e) => setIg(e.target.value)} placeholder="@their_handle or instagram.com/their_handle" autoComplete="off" />
        </label>
        {!own && <LevelSelect value={level} onChange={setLevel} />}
        <label className="check-row competitors-own">
          <input type="checkbox" checked={own} disabled={hasSelf} onChange={(e) => setOwn(e.target.checked)} />
          <span>This is {clientName}'s own page{hasSelf ? " (already added)" : ""}</span>
        </label>
        {err && <p className="inline-notice danger" role="alert">{err}</p>}
        <div className="operation-actions">
          <button type="button" className="button secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="button" disabled={busy}>{busy ? "Adding…" : "Add"}</button>
        </div>
      </form>
    </Dialog>
  );
}

function EditDialog({ c, onClose, onSaved }: { c: Competitor; onClose: () => void; onSaved: (row: Competitor) => void }) {
  const [label, setLabel] = useState(c.label);
  const [level, setLevel] = useState<number | null>(c.level);
  const [ig, setIg] = useState(c.instagram_handle ? `@${c.instagram_handle}` : "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const igChanged = ig.trim().replace(/^@/, "").toLowerCase() !== (c.instagram_handle || "");
  const changed = label.trim() !== c.label || level !== c.level || igChanged;
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!changed) return onClose();
    setBusy(true);
    setErr("");
    try {
      onSaved(await api<Competitor>(`/api/competitors/${c.id}`, { method: "PATCH", body: JSON.stringify({ label, ...(c.kind === "self" ? {} : { level }), ...(igChanged ? { instagram_handle: ig.trim() || null } : {}) }) }));
    } catch (reason) {
      setErr(reason instanceof Error ? reason.message : "Could not save.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog title="Edit" description={c.page_name && c.page_name !== c.label ? `Facebook page: ${c.page_name}` : undefined} onClose={onClose}>
      <form className="operation-form competitors-form" onSubmit={submit}>
        <label>Name<input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={120} /></label>
        <label><span>Instagram <em>optional</em></span><input value={ig} onChange={(e) => setIg(e.target.value)} placeholder="@their_handle" autoComplete="off" /></label>
        {c.kind !== "self" && <LevelSelect value={level} onChange={setLevel} />}
        {err && <p className="inline-notice danger" role="alert">{err}</p>}
        <div className="operation-actions">
          <button type="button" className="button secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="button" disabled={busy || !changed || !label.trim()}>{busy ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </Dialog>
  );
}

function RemoveDialog({ c, onClose, onRemoved }: { c: Competitor; onClose: () => void; onRemoved: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  async function remove() {
    setBusy(true);
    setErr("");
    try {
      await api(`/api/competitors/${c.id}`, { method: "DELETE" });
      onRemoved();
    } catch (reason) {
      setErr(reason instanceof Error ? reason.message : "Could not remove.");
      setBusy(false);
    }
  }
  return (
    <Dialog title={`Remove ${c.kind === "self" ? "your own page" : c.label}?`} onClose={onClose}>
      <div className="operation-form">
        <p className="competitors-dialog-copy">Their saved ads and check history are removed too, and weekly checks stop.</p>
        {err && <p className="inline-notice danger" role="alert">{err}</p>}
        <div className="operation-actions">
          <button type="button" className="button secondary" onClick={onClose}>Keep</button>
          <button type="button" className="button danger" disabled={busy} onClick={remove}>{busy ? "Removing…" : "Remove"}</button>
        </div>
      </div>
    </Dialog>
  );
}
